import { useState, useCallback } from 'react';
import { get, post, put, del } from '../../utils/networkUtils';

export const usePrizeDraws = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get all prize draws
  const fetchPrizeDraws = useCallback(async (limit = 1000, offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/raffles?limit=${limit}&offset=${offset}`);
      return response ? response.filter(Boolean) : [];
    } catch (err) {
      setError(err.message || 'Failed to fetch prize draws');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get single prize draw by ID
  const fetchPrizeDrawById = useCallback(async (raffleId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/raffles/${raffleId}`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to fetch prize draw');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new prize draw
  const createPrizeDraw = useCallback(async (prizeDrawData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post('/admin/prize-draws', prizeDrawData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to create prize draw');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update prize draw
  const updatePrizeDraw = useCallback(async (raffleId, prizeDrawData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await put(`/admin/prize-draws/${raffleId}`, prizeDrawData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update prize draw');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete prize draw
  const deletePrizeDraw = useCallback(async (raffleId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await del(`/admin/prize-draws/${raffleId}`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to delete prize draw');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete prize draw entries
  const deletePrizeDrawEntries = useCallback(async (userIds) => {
    setLoading(true);
    setError(null);
    try {
      const response = await del(`/admin/prize-draws/entries/${userIds}`);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to delete prize draw entries');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Export prize draw as CSV
  const exportPrizeDrawCsv = useCallback(async (raffleId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await get(`/admin/prize-draws/csv/${raffleId}`, { 
        responseType: 'blob' 
      });
      return response;
    } catch (err) {
      setError(err.message || 'Failed to export prize draw');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Refund tickets
  const refundTickets = useCallback(async (refundData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await post('/admin/tickets/refund', refundData);
      return response;
    } catch (err) {
      setError(err.message || 'Failed to refund tickets');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    fetchPrizeDraws,
    fetchPrizeDrawById,
    createPrizeDraw,
    updatePrizeDraw,
    deletePrizeDraw,
    deletePrizeDrawEntries,
    exportPrizeDrawCsv,
    refundTickets,
  };
};
